import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ThumbIconButton from '@wix/da-ds/pkg/thumbs/ThumbIconButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import CommentOutlineIcon from '@wix/da-ds/pkg/Icons/24x24/CommentOutline';
import { BiData, ThumbClickCommentBiEvent, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import { getDeviationResourceType } from '../../../../_helpers';
import noop from '../../../../../utils/noop';
import { useSubscriptionTier } from '../../../../../utils/hooks/useSubscriptionTier';
import { useDeviationAttributes } from '../../../../hooks/useDeviationAttributes';
import SubscribeToUnlockTooltip from '../../../../../ActionBar/_partials/SubscribeToUnlockTooltip';

export interface Props {
  deviation: PapiDeviation;
  className?: string;
  iconClassName?: string;
  textClassName?: string;
  disabledClassName?: string;
  onClick?: () => void;
}

const CommentControl: React.FC<Props> = ({
  deviation,
  className,
  iconClassName,
  textClassName,
  disabledClassName,
  onClick = noop,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { isOwner, needsSubscription, isSubscribedButBlocked } = useSubscriptionTier(deviation);
  const { isCommentable } = useDeviationAttributes(deviation, isOwner);
  if (!isCommentable) {
    return null;
  }

  const biData = BiData<ThumbClickCommentBiEvent>({
    evid: 105,
    typeid: getDeviationResourceType(deviation),
    itemid: deviation.deviationId,
    creatorid: deviation.author?.useridUuid ?? '',
    sectionname: BiContextProvidedValue,
    sectionid: BiContextProvidedValue,
    section_typeid: BiContextProvidedValue,
    sectiontype: BiContextProvidedValue,
    sectionrank: BiContextProvidedValue,
    rank: BiContextProvidedValue,
    sort_type: BiContextProvidedValue,
    reason_shown: BiContextProvidedValue,
    widgetname: BiContextProvidedValue,
  });

  const actionBlockedBySubscription = needsSubscription || isSubscribedButBlocked;

  if (isMobile) {
    return (
      <ThumbIconButton
        type="comment"
        statValue={deviation.stats?.comments}
        onClick={actionBlockedBySubscription ? undefined : onClick}
        biData={biData}
      />
    );
  }

  return (
    <SubscribeToUnlockTooltip deviation={deviation}>
      <TextButton
        className={classnames(actionBlockedBySubscription && disabledClassName, className)}
        size="medium"
        variant="main"
        onClick={actionBlockedBySubscription ? undefined : onClick}
        disabled={actionBlockedBySubscription}
        biData={biData}
      >
        <CommentOutlineIcon className={iconClassName} size={IconSize.MEDIUM} disableDefaultColors />
        <span className={textClassName}>{t('actionbar.comment')}</span>
      </TextButton>
    </SubscribeToUnlockTooltip>
  );
};
CommentControl.displayName = 'CommentControl';

export default CommentControl;
